import React, { useMemo } from 'react';

interface MemberAvatarProps {
  avatarUrl?: string | null;
  name: string;
  color?: string | null;
  size?: 'xs' | 'sm' | 'md' | 'lg' | 'xl';
  role?: string;
  className?: string;
}

const sizeClasses = {
  xs: 'w-6 h-6 text-xs',
  sm: 'w-8 h-8 text-sm',
  md: 'w-10 h-10 text-base',
  lg: 'w-12 h-12 text-lg',
  xl: 'w-16 h-16 text-xl',
};

const roleColors: Record<string, string> = {
  owner: 'from-primary-500 to-accent-500',
  admin: 'from-amber-500 to-orange-500',
  member: 'from-emerald-500 to-teal-500',
  child: 'from-cyan-500 to-blue-500',
  guest: 'from-surface-500 to-surface-600',
};

export default function MemberAvatar({
  avatarUrl,
  name,
  color,
  size = 'md',
  role = 'member',
  className = '',
}: MemberAvatarProps) {
  const sizeClass = sizeClasses[size];
  const initials = name
    .split(' ')
    .map((n) => n[0])
    .slice(0, 2)
    .join('')
    .toUpperCase();

  // Resolve the full avatar URL (handle both absolute and relative paths)
  const resolvedAvatarUrl = useMemo(() => {
    if (!avatarUrl) return null;
    if (avatarUrl.startsWith('http')) return avatarUrl;
    const apiBase = (import.meta.env.VITE_API_URL || '').replace(/\/$/, '');
    return `${apiBase}${avatarUrl}`;
  }, [avatarUrl]);

  if (resolvedAvatarUrl) {
    return (
      <img
        src={resolvedAvatarUrl}
        alt={name}
        className={`${sizeClass} rounded-full object-cover flex-shrink-0 ${className}`}
      />
    );
  }

  // Use custom color if provided, otherwise use role-based gradient
  const bgStyle = color
    ? { background: color }
    : undefined;

  const bgClass = color
    ? ''
    : `bg-gradient-to-br ${roleColors[role] || roleColors.member}`;

  return (
    <div
      className={`${sizeClass} ${bgClass} rounded-full flex items-center justify-center text-white font-semibold flex-shrink-0 ${className}`}
      style={bgStyle}
    >
      {initials || '?'}
    </div>
  );
}
